import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Divider } from '@mui/material';
import {
  ApiButton,
  Box,
  ContentTemplate,
  HeaderTemplate,
  KeyValue,
  SectionHeader,
  Stepper,
  Typography,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useGetUploadTemplateDownloadUrl } from '../../../api-refactored/group-exam-registration/useGetUploadTemplateDownloadUrl';
import { useGetGroupRegistrationSummary } from '../../../api-refactored/group-exam-registration/useGetRegistrationSummary';
import UploadSection from './UploadSection';
import { usePutStartFileProcessing } from '../../../api-refactored/group-exam-registration/usePutStartFileProcessing';
import { FileProcessingState } from '../../../models/File';
import GroupExamRegistrationTitleHeader from '../../../components/GroupExamRegistrationTitleHeader';
import { ComponentFileProcessingState } from './UploadSection/model';
import DraftTimeStamp from '../../../components/DraftTimeStamp';
import RouteKey from '../../../routes/key';
import {typeTheme} from "web-library/theme/helper/tssHelper"

interface CoordinatorBulkUploadCandidatesPageProps {
  routeSteps: { label: string }[];
}

const useStyles = makeStyles()((theme) =>
({
  root: {
    '& .MuiPaper-root.MuiStepper-root': {
      [theme.breakpoints.up('md')]: {
        marginLeft: '-70px',
      },
    },
  },
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  instruction: {
    marginBottom: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(4),
  },
  dropZone: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  dropZoneHelperTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },
  dropZoneHelperText: {
    color: theme.color.text.textSecondary,
  },
  attachedFilesHeader: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
  fileBadgeParentContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flexWrap: 'nowrap'
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  filename: {
    width: theme.spacing(60),
    textOverflow: 'ellipsis',
    flexGrow: 2,
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
  },

  fileSize: {
    width: theme.spacing(20),
    justifySelf: 'start',
    color: theme.color.text.textTertiary,
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
  removeCell: {
    width: theme.spacing(12),
  },
  removeButton: {
    padding: 0,
    height: theme.spacing(4),
    color: theme.color.icon.iconTrash,
  },
  buttonContainer: {
    display: 'flex',
    marginTop: theme.spacing(6),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  uploadingIcon: {
    '& svg': {
      color: theme.color.icon.iconDefault,
    },
  },
  uploadErrorSection: {
    '& img': {
      width: 45,
      height: 45,
    },
  },
  errorProcessingContainer: {
    marginBottom: theme.spacing(4),
  },
  uploadErrorDescription: {
     ...typeTheme(theme, 'body1bold'),
  },
  stopProcessingButton: {
    color: theme.color.state.stateError,
  },
  stopProcessingIcon: {
    color: theme.color.state.stateError,
  },
  buttonRow: {
    marginTop: theme.spacing(6),
  },
}));

const { exam, error } = RouteKey.child;
const { authorized } = RouteKey.error.child;
const { invalid, coordinatorExamRegistrations } = RouteKey.exam.child;

const CoordinatorBulkUploadCandidates = ({
  routeSteps,
}: CoordinatorBulkUploadCandidatesPageProps) => {
  const [
    isFileProcessedSuccess,
    setIsFileProcessedSuccess,
  ] = useState<ComponentFileProcessingState>(ComponentFileProcessingState.IDLE);
  const { uuid: applicationUuid } = useParams<any>();
  const { classes } = useStyles();
  const history = useHistory();
  const { state } = useLocation<{ token: string }>();
  const { t } = useTranslation();
  const startFileProcessingMutation = usePutStartFileProcessing({
    onSuccess: () => {
      setIsFileProcessedSuccess(ComponentFileProcessingState.PROCESSING);
      getGroupRegistrationSummaryQuery.refetch();
    },
  });

  const getGroupRegistrationSummaryQuery = useGetGroupRegistrationSummary({
    uuid: applicationUuid,
    onSuccess: (res) => {
      if (res.fileStatus === FileProcessingState.SUCCESS) {
        setIsFileProcessedSuccess(ComponentFileProcessingState.SUCCESS);
      }
      if (res.fileStatus === FileProcessingState.ERROR) {
        setIsFileProcessedSuccess(ComponentFileProcessingState.ERROR);
      }
    },
    onError: (err) => {
      if (err.response?.data.code === '2011') {
        history.push(
          `/${exam}/${coordinatorExamRegistrations.route}/${applicationUuid}/${invalid.route}`
        );
      } else if (err.response?.data.code === '7001') {
        history.push(`/${error}`);
      } else if (err.response?.data.code === '7002') {
        history.push(`/${error}/${authorized}`);
      } else {
        history.push(`/${error}`);
      }
    },
    isRedirect: false,
    refetchInterval:
      isFileProcessedSuccess === ComponentFileProcessingState.PROCESSING ? 5000 : undefined,
  });

  const { data: summaryResponse } = getGroupRegistrationSummaryQuery;
  const examType = summaryResponse?.examType.code;
  const getUploadTemplateDownloadUrl = useGetUploadTemplateDownloadUrl({
    uuid: applicationUuid,
    examType,
    onSuccess: (res) => {
      const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
  });
  const downloadUploadTemplate = () =>
    summaryResponse?.examType.code && getUploadTemplateDownloadUrl.refetch();

  return (
    <Box className={classes.root}>
      <GroupExamRegistrationTitleHeader
        examRegistrationType={summaryResponse?.examRegistrationType}
        t={t}
      />
      <HeaderTemplate>
        <KeyValue
          keyText={t('exam->>RegisterExamInstructions.Exam Level')}
          valueText={summaryResponse?.examType.description}
        />
        <Stepper activeStep={0} steps={routeSteps} />
      </HeaderTemplate>
      <ContentTemplate type="full" isContinuable>
        <Box>
          <SectionHeader title={t('exam->>UploadExcelTemplate.UploadExcelTemplate')} />
          <Typography variant="body1" className={classes.instruction}>
            {t('exam->>UploadExcelTemplate.DownloadAndFillUp')}
          </Typography>
          <ApiButton
            id="button-download-upload-template"
            status={getUploadTemplateDownloadUrl.status}
            variant="outlined"
            size="medium"
            onClick={downloadUploadTemplate}
          >
            <IconWithLabel
              iconName={IconName.download}
              iconSize="M"
              label={t('exam->>UploadExcelTemplate.DownloadExcelTemplate')}
              typographyProps={{
                variant: 'h5',
                color: 'inherit',
              }}
            />
          </ApiButton>
        </Box>
        <Divider className={classes.divider} />
      </ContentTemplate>
      <ContentTemplate continuous>
        {summaryResponse && (
          <>
            <UploadSection
              classes={classes}
              translation={t}
              isFileProcessedSuccess={isFileProcessedSuccess}
              apiHooks={{ getGroupRegistrationSummaryQuery, startFileProcessingMutation }}
              stateHandler={{
                setIsFileProcessedHandler: {
                  isFileProcessedSuccess,
                  setIsFileProcessedSuccess,
                },
              }}
              token={state && state.token}
            />
            <DraftTimeStamp savedAt={summaryResponse.lastUpdatedTime} />
          </>
        )}
      </ContentTemplate>
    </Box>
  );
};

export default CoordinatorBulkUploadCandidates;
